import React from 'react';

import { UxContext } from '@client/context/UxContext';
import i18n from '@client/utils/i18n';
import lediumApi from '@client/utils/lediumApi';
import type { ChannelMapEntry, DmxResolution, LinkedZoneDoc } from '@ledium/types';
import { LinkTypeChip } from '@ledium/ui-common';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';

import ChannelMapEditDialog from './ChannelMapEditDialog';

interface UsedLightChannel {
    id: string;
    name: string;
    dmxAddress: number;
    resolution: DmxResolution;
}

interface LinkHealth {
    mapped: number;
    total: number;
    orphans: number;
    unmapped: number;
}

export function computeHealth(link: LinkedZoneDoc, usedChannels: UsedLightChannel[]): LinkHealth {
    const usedIds = new Set(usedChannels.map((c) => c.id));
    const mapIds = new Set(link.channelMap.map((e: ChannelMapEntry) => e.sourceChannelId));
    const orphans = link.channelMap.filter((e: ChannelMapEntry) => !usedIds.has(e.sourceChannelId)).length;
    const unmapped = usedChannels.filter((c) => !mapIds.has(c.id)).length;
    const mapped = link.channelMap.length - orphans;
    return { mapped, total: usedChannels.length, orphans, unmapped };
}

interface LinkedProjectRowProps {
    link: LinkedZoneDoc;
    usedChannels: UsedLightChannel[];
    onRefresh: () => void;
    onEdit?: (link: LinkedZoneDoc) => void;
}

function LinkedProjectRow({ link, usedChannels, onRefresh, onEdit }: LinkedProjectRowProps) {
    const { handleAlert, handleSnackbar } = React.useContext(UxContext)!;
    const [editOpen, setEditOpen] = React.useState(false);
    const health = computeHealth(link, usedChannels);

    const isMappingError = !link.enabled && link.disabledReason === 'mapping_error';
    const isUserDisabled = !link.enabled && link.disabledReason === 'user';

    async function handleDeleteClick() {
        if (link.enabled) {
            const running = await lediumApi.get<{ name: string | null }>(`zones/${link.sourceZoneId}/runningProject`);
            if (running?.name) {
                handleAlert({
                    title: i18n('linked_zone_delete_running_title') as string,
                    message: i18n('linked_zone_delete_running_message', running.name) as string,
                    destructive: true,
                    okCallback: async () => {
                        await lediumApi.delete(`linkedZones/${link._id}`);
                        handleSnackbar({ message: i18n('linked_project_deleted') as string });
                        onRefresh();
                    }
                });
                return;
            }
        }
        handleAlert({
            title: i18n('linked_project_delete_title') as string,
            message: i18n('linked_project_delete_message') as string,
            destructive: true,
            okCallback: async () => {
                await lediumApi.delete(`linkedZones/${link._id}`);
                handleSnackbar({ message: i18n('linked_project_deleted') as string });
                onRefresh();
            }
        });
    }

    return (
        <Box sx={{ pl: 4, py: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap>
                    {link.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {i18n('linked_project_health', health.mapped, health.total) as string}
                </Typography>
            </Box>

            <LinkTypeChip type={link.type} />
            {link.enabled && <Chip color="success" size="small" label={i18n('linked_project_status_enabled') as string} />}
            {isMappingError && <Chip color="warning" size="small" label={i18n('linked_project_status_mapping_error') as string} />}
            {isUserDisabled && <Chip size="small" label={i18n('linked_project_status_disabled') as string} />}

            <Tooltip title={i18n('linked_project_edit_mapping')}>
                <IconButton size="small" aria-label={i18n('linked_project_edit_mapping') as string} onClick={() => (onEdit ? onEdit(link) : setEditOpen(true))}>
                    <EditIcon fontSize="small" />
                </IconButton>
            </Tooltip>

            <Tooltip title={i18n('delete')}>
                <IconButton size="small" color="error" aria-label={i18n('delete') as string} onClick={handleDeleteClick}>
                    <DeleteIcon fontSize="small" />
                </IconButton>
            </Tooltip>

            <ChannelMapEditDialog
                open={editOpen}
                link={link}
                usedChannels={usedChannels}
                onClose={() => setEditOpen(false)}
                onSaved={() => {
                    setEditOpen(false);
                    onRefresh();
                }}
            />
        </Box>
    );
}

export default LinkedProjectRow;
